import { supabase } from '../lib/supabase';

export const listingsService = {
  // Create a new listing
  async createListing(listingData) {
    try {
      const { data, error } = await supabase
        .from('listings')
        .insert([{
          ...listingData,
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating listing:', error);
      throw error;
    }
  },

  // Get all listings (only active ones)
  async getListings() {
    try {
      console.log('Fetching listings...');
      
      // Try to get active listings first, fallback to all if status column doesn't exist
      let { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      // If error due to status column not existing, try without status filter
      if (error && error.message.includes('status')) {
        console.log('Status column not found, fetching all listings...');
        const fallbackResult = await supabase
          .from('listings')
          .select('*')
          .order('created_at', { ascending: false });
        
        data = fallbackResult.data;
        error = fallbackResult.error;
      }

      console.log('Listings response:', { data, error });
      
      if (error) {
        console.error('Supabase error:', error);
        // If the error is about the table not existing, let's try to create it
        if (error.message.includes('relation "public.listings" does not exist')) {
          console.log('Listings table does not exist. Please run the database setup script.');
          throw new Error('Database not set up. Please run the SQL script in Supabase.');
        }
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching listings:', error);
      throw error;
    }
  },

  // Get user's own listings
  async getUserListings(userId) {
    try {
      console.log('Fetching user listings for:', userId);
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      console.log('User listings response:', { data, error });
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching user listings:', error);
      throw error;
    }
  },

  // Get a single listing by ID (including sold items)
  async getListing(id) {
    try {
      console.log('Fetching listing:', id);
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('id', id)
        .single();

      console.log('Listing response:', { data, error });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Fetch seller profile separately
      if (data) {
        const { data: sellerProfile, error: profileError } = await supabase
          .from('profiles')
          .select('name, avatar_url')
          .eq('id', data.user_id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') { // PGRST116 means no rows found
          console.error('Error fetching seller profile:', profileError);
        }
        
        // Add profile data to listing
        data.seller_profile = sellerProfile || { 
          id: data.user_id, 
          name: `User ${data.user_id.slice(0, 8)}`, 
          avatar_url: null 
        };
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching listing:', error);
      throw error;
    }
  },

  // Upload image to Supabase Storage
  async uploadImage(file, listingId) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${listingId}_${Date.now()}.${fileExt}`;
      const filePath = `listings/${fileName}`;

      const { data, error } = await supabase.storage
        .from('listing-images')
        .upload(filePath, file);

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('listing-images')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },

  // Update a listing
  async updateListing(id, listingData) {
    try {
      const { data, error } = await supabase
        .from('listings')
        .update({
          ...listingData,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating listing:', error);
      throw error;
    }
  },

  // Delete a listing
  async deleteListing(id) {
    try {
      const { error } = await supabase
        .from('listings')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting listing:', error);
      throw error;
    }
  }
};
